import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props {
  title?: string
  description?: string
  onComplete?: () => void
  onAction?: (event: { action: string; parameters?: Record<string, number | boolean> }) => void
}

// Dao động tắt dần, cưỡng bức & cộng hưởng (Lớp 11 — Bài 4)
// Biên độ dao động cưỡng bức ổn định: A(ω) = 1 / √[(ω₀²−ω²)² + (2βω)²]
// ω → ω₀ ⇒ A cực đại (CỘNG HƯỞNG); ma sát β càng lớn ⇒ đỉnh càng thấp & tù.
const W = 620, H = 170, GW = 620, GH = 190
const W0 = 1.0 // tần số riêng (chuẩn hoá)
const amp = (w: number, b: number) => 1 / Math.sqrt((W0 * W0 - w * w) ** 2 + (2 * b * w) ** 2)

const DrivenResonanceSimulation: React.FC<Props> = ({
  title = 'Dao động cưỡng bức & cộng hưởng',
  description = 'Tần số lực cưỡng bức ω tiến tới tần số riêng ω₀ → biên độ tăng vọt (cộng hưởng). Ma sát (tắt dần) càng lớn, đỉnh cộng hưởng càng thấp và tù.',
  onComplete,
  onAction,
}) => {
  const top = useRef<HTMLCanvasElement>(null)
  const gcv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [wf, setWf] = useState(0.7)
  const [beta, setBeta] = useState(0.12)
  const [running, setRunning] = useState(true)
  const wfRef = useRef(wf), betaRef = useRef(beta), runRef = useRef(running)
  const firstRef = useRef(false)
  useEffect(() => { wfRef.current = wf }, [wf])
  useEffect(() => { betaRef.current = beta }, [beta])
  useEffect(() => { runRef.current = running }, [running])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const draw = useCallback(() => {
    const ctx = prepCanvas(top.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const b = betaRef.current, w = wfRef.current
    const A = amp(w, b), Apk = amp(W0, b)
    const disp = (A / Apk) * 78
    const cx = W / 2, cy = 72
    const x = cx + disp * Math.cos(tRef.current * w)
    // ray + VTCB
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(60, cy + 22, W - 120, 5)
    ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.moveTo(cx, cy - 34); ctx.lineTo(cx, cy + 30); ctx.stroke(); ctx.setLineDash([])
    // lò xo (zigzag) từ tường trái tới vật
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(60, cy)
    const n = 16; for (let i = 1; i < n; i++) { const px = 60 + ((x - 20) - 60) * (i / n); ctx.lineTo(px, cy + (i % 2 ? -9 : 9)) }
    ctx.lineTo(x - 20, cy); ctx.stroke()
    ctx.fillStyle = '#1e293b'; ctx.fillRect(56, cy - 26, 6, 52) // tường
    // vật
    const g = ctx.createLinearGradient(x - 20, cy - 20, x + 20, cy + 20); g.addColorStop(0, '#60a5fa'); g.addColorStop(1, '#1d4ed8')
    ctx.fillStyle = g; ctx.fillRect(x - 20, cy - 20, 40, 40)
    ctx.fillStyle = '#334155'; ctx.font = 'bold 13px sans-serif'
    ctx.fillText(`ω = ${w.toFixed(2)}   ·   A ≈ ${(A / Apk).toFixed(2)} (so với cộng hưởng)`, 14, H - 14)
    if (Math.abs(w - W0) < 0.06) { ctx.fillStyle = '#dc2626'; ctx.font = 'bold 16px sans-serif'; ctx.fillText('CỘNG HƯỞNG!', cx - 56, 24) }
  }, [])

  const drawGraph = useCallback(() => {
    const ctx = prepCanvas(gcv.current, GW, GH); if (!ctx) return
    ctx.clearRect(0, 0, GW, GH)
    const b = betaRef.current, x0 = 46, y0 = GH - 26, plotW = GW - x0 - 16, plotH = GH - 44, peak = amp(W0, b), wmax = 2.2
    const X = (w: number) => x0 + (w / wmax) * plotW
    const Y = (a: number) => y0 - (a / peak) * plotH * 0.92
    // lưới + nhãn ω
    ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1; ctx.fillStyle = '#94a3b8'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center'
    for (let w = 0.5; w <= wmax; w += 0.5) { ctx.beginPath(); ctx.moveTo(X(w), 12); ctx.lineTo(X(w), y0); ctx.stroke(); ctx.fillText(w.toFixed(1), X(w), y0 + 14) }
    ctx.textAlign = 'left'
    // trục đậm + mũi tên
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x0, 12); ctx.lineTo(x0, y0); ctx.lineTo(x0 + plotW, y0); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, 6); ctx.lineTo(x0 - 4, 14); ctx.lineTo(x0 + 4, 14); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(x0 + plotW + 6, y0); ctx.lineTo(x0 + plotW - 3, y0 - 4); ctx.lineTo(x0 + plotW - 3, y0 + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('A', x0 - 2, 11); ctx.fillText('ω', x0 + plotW + 2, y0 + 16)
    // vạch ω₀
    ctx.strokeStyle = 'rgba(220,38,38,.45)'; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.moveTo(X(W0), 12); ctx.lineTo(X(W0), y0); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#dc2626'; ctx.font = '12px sans-serif'; ctx.fillText('ω₀', X(W0) - 8, 22)
    // đường cong cộng hưởng
    ctx.strokeStyle = '#7c3aed'; ctx.lineWidth = 2.5; ctx.beginPath()
    for (let i = 0; i <= 170; i++) { const w = (i / 170) * wmax; const px = X(w), py = Y(amp(w, b)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
    ctx.stroke()
    // marker ω cưỡng bức
    const w = wfRef.current
    ctx.fillStyle = '#16a34a'; ctx.beginPath(); ctx.arc(X(w), Y(amp(w, b)), 5, 0, Math.PI * 2); ctx.fill()
  }, [])

  const tick = useCallback(() => {
    if (runRef.current) tRef.current += 0.05
    draw(); drawGraph()
    raf.current = requestAnimationFrame(tick)
  }, [draw, drawGraph])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={top} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <canvas ref={gcv} width={GW} height={GH} className="w-full mt-2 rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-32 text-gray-600">Tần số cưỡng bức ω</span><input type="range" min={0.2} max={2.0} step={0.02} value={wf} onChange={(e) => { touch(); setWf(Number(e.target.value)) }} onPointerUp={() => onAction?.({ action: 'frequency_changed', parameters: { frequency: wf, damping: beta } })} onKeyUp={() => onAction?.({ action: 'frequency_changed', parameters: { frequency: wf, damping: beta } })} className="flex-1 accent-violet-500" /><span className="w-10 text-right font-semibold">{wf.toFixed(2)}</span></label>
        <label className="flex items-center gap-2"><span className="w-32 text-gray-600">Ma sát (tắt dần) β</span><input type="range" min={0.04} max={0.6} step={0.02} value={beta} onChange={(e) => { touch(); setBeta(Number(e.target.value)) }} onPointerUp={() => onAction?.({ action: 'damping_changed', parameters: { frequency: wf, damping: beta } })} onKeyUp={() => onAction?.({ action: 'damping_changed', parameters: { frequency: wf, damping: beta } })} className="flex-1" /><span className="w-10 text-right font-semibold">{beta.toFixed(2)}</span></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); setRunning((r) => { const next = !r; onAction?.({ action: next ? 'played' : 'paused', parameters: { frequency: wf, damping: beta } }); return next }) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">{running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={() => { touch(); setWf(1.0); onAction?.({ action: 'resonance_selected', parameters: { frequency: 1, damping: beta } }) }} className="px-4 py-2 rounded-lg border border-red-200 text-red-600 font-bold hover:bg-red-50">Đặt ω = ω₀ (cộng hưởng)</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Cộng hưởng: ω = ω₀ → biên độ lớn nhất. Giảm ma sát → đỉnh cao & nhọn; tăng ma sát → đỉnh thấp & tù (dao động tắt dần nhanh).</p>
    </div>
  )
}
export default DrivenResonanceSimulation
